// Slug helpers for hotel-scoped Experiences & Nearby (Destination) items.
//
// Historically each excursion/nearby entry was linked by array index —
// `/hotels/<slug>/excursions/2`. Reordering the source data silently broke
// every bookmark, shared link and analytics segment. This helper derives a
// stable, human-readable slug from the item's English title (falling back
// to any other locale) so the URL is meaningful AND resilient to reorder:
// `/hotels/<slug>/excursions/atv-dune-ride`.
//
// `resolveActivity(list, slugOrIdx)` returns the matching item using the
// following priority:
//   1. Exact slug match against every localized title (en/fr/es).
//   2. Numeric index fallback — preserves compatibility with older URLs
//      still floating around in emails, chat threads and search engines.
//
// Both helpers are pure and safe to call with sparse/undefined data.

export const slugifyActivity = (title) => {
  if (!title) return "";
  const raw =
    typeof title === "string"
      ? title
      : title.en || title.fr || title.es || Object.values(title).find(Boolean) || "";
  return String(raw)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip accents
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
};

// Given a list of activity entries and a URL parameter (slug or numeric
// index), return `{ item, index }` or `null` when nothing matches. The
// search runs in three passes: exact slug on any locale, exact slug on
// the localized title, then numeric index.
export const resolveActivity = (list, slugOrIdx) => {
  if (!Array.isArray(list) || !list.length || !slugOrIdx) return null;
  const target = String(slugOrIdx);

  // 1. Slug match across every locale of the `title` bag.
  for (let i = 0; i < list.length; i++) {
    const t = list[i]?.title;
    if (!t) continue;
    const bag = typeof t === "string" ? { en: t } : t;
    for (const key of Object.keys(bag)) {
      if (slugifyActivity(bag[key]) === target) return { item: list[i], index: i };
    }
  }

  // 2. Numeric index fallback (legacy URLs).
  if (/^\d+$/.test(target)) {
    const idx = parseInt(target, 10);
    if (idx >= 0 && idx < list.length) return { item: list[idx], index: idx };
  }

  return null;
};
